import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { InsumoVista, UnidadInsumo, UnidadMedidaVista } from '@shared/types';
import { NumberField } from '@renderer/components/ui/NumberField';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { conConfirmacionDeNombre } from '@renderer/lib/confirmarNombreParecido';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import {
  describirFraccion,
  esIndivisible,
  fraccionSugeridaPorUnidad,
} from '@shared/fraccionInsumo';

interface Props {
  abierto: boolean;
  insumo: InsumoVista | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Sprint 63 (Fase 4) — Las unidades vienen del catálogo, no del código.
 *
 * Eran tres valores fijos y el dueño preguntó con un "?" sobre el desplegable dónde se
 * daban de alta. Ahora se administran en Configuración → Unidades de medida, y esta
 * pantalla sólo las lee: cada una trae su nombre, su explicación y la divisibilidad que
 * se propone.
 */

/**
 * Sprint 13 — FormInsumo unificado con merma por cocción.
 *
 * La merma vive en el INSUMO (atributo estructural), no en cada carga diaria.
 * Si cambia el proveedor o el corte, se edita aquí y aplica a todas las
 * ventas futuras automáticamente.
 *
 * Para insumos que NO se cocinan (pan, refresco, salsa), merma = 0.
 */
export function FormInsumo({ abierto, insumo, onClose, onSaved }: Props) {
  const sesion = useSessionStore((s) => s.sesion);
  const [nombre, setNombre] = useState('');
  const [unidad, setUnidad] = useState<UnidadInsumo>('kg');
  const [stockMinimo, setStockMinimo] = useState(0);
  const [mermaCoccionPct, setMermaCoccionPct] = useState(0);
  /**
   * Sprint 61 — En qué cantidades se puede usar este insumo.
   *
   * Nace del reporte "¿compré media lata de Coca Cola?": el inventario había
   * quedado en 48.5 piezas porque un porcentaje de merma se aplicó sobre algo que
   * no se parte. Pero tampoco todas las piezas son iguales — el pollo se vende
   * entero o en mitad, y quizá mañana en cuartos —, así que lo decide el negocio.
   */
  const [fraccionMinima, setFraccionMinima] = useState<number | null>(null);
  const [unidades, setUnidades] = useState<UnidadMedidaVista[]>([]);
  const [cantidadInicial, setCantidadInicial] = useState(0);
  const [activo, setActivo] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    void window.api.unidades.listar().then((res) => {
      if (res.ok) setUnidades(res.data);
    });
  }, [abierto]);

  useEffect(() => {
    if (insumo) {
      setNombre(insumo.nombre);
      setUnidad(insumo.unidad);
      setStockMinimo(insumo.stockMinimo);
      setMermaCoccionPct(insumo.mermaCoccionPct ?? 0);
      setFraccionMinima(insumo.fraccionMinima ?? null);
      setActivo(insumo.activo);
      setCantidadInicial(0);
    } else {
      setNombre('');
      setUnidad('kg');
      setStockMinimo(0);
      setMermaCoccionPct(0);
      setFraccionMinima(fraccionSugeridaPorUnidad('kg'));
      setActivo(true);
      setCantidadInicial(0);
    }
    setError(null);
  }, [insumo, abierto]);

  if (!abierto) return null;

  /**
   * Opciones de divisibilidad. Si el insumo trae un valor que no está en la lista
   * (alguien lo puso por script, o una versión futura), se agrega tal cual: un
   * `select` sin opción coincidente se muestra vacío y al guardar cambiaría el dato
   * sin que nadie lo pidiera.
   */
  const OPCIONES_FRACCION: Array<{ valor: number; etiqueta: string }> = [
    { valor: 1, etiqueta: 'Sólo completo (una lata, un pan, una bolsa)' },
    { valor: 0.5, etiqueta: 'Se parte en mitades (medio pollo)' },
    { valor: 0.25, etiqueta: 'Se parte en cuartos' },
    { valor: 0.125, etiqueta: 'Se parte en octavos' },
    { valor: 0.05, etiqueta: 'Se sirve por copas (20 por envase)' },
  ];
  const opcionesFraccion =
    fraccionMinima !== null && !OPCIONES_FRACCION.some((o) => o.valor === fraccionMinima)
      ? [
          ...OPCIONES_FRACCION,
          { valor: fraccionMinima, etiqueta: describirFraccion(fraccionMinima, unidad) },
        ]
      : OPCIONES_FRACCION;

  /**
   * Al cambiar de unidad se propone su divisibilidad habitual.
   *
   * Sprint 63 — La propuesta viene del catálogo (`fraccionSugerida`), así que agregar
   * "bolsa" incluye decir cómo se usa una bolsa. Si el catálogo no lo dice, se cae a la
   * regla del Sprint 61 por compatibilidad.
   */
  const cambiarUnidad = (u: UnidadInsumo) => {
    setUnidad(u);
    if (insumo) return;
    const cat = unidades.find((x) => x.codigo === u);
    setFraccionMinima(cat ? cat.fraccionSugerida : fraccionSugeridaPorUnidad(u));
  };

  const submit = async () => {
    if (nombre.trim().length < 2) {
      setError('El nombre debe tener al menos 2 caracteres');
      return;
    }
    setEnviando(true);
    setError(null);
    const res = insumo
      ? await window.api.insumos.actualizar(insumo.id, {
          nombre: nombre.trim(),
          unidad,
          stockMinimo,
          mermaCoccionPct,
          fraccionMinima,
          activo,
        })
      : await conConfirmacionDeNombre((confirmado) => window.api.insumos.crear({
          nombreParecidoConfirmado: confirmado || undefined,
          nombre: nombre.trim(),
          unidad,
          stockMinimo,
          mermaCoccionPct,
          fraccionMinima,
          cantidadInicial: cantidadInicial > 0 ? cantidadInicial : undefined,
          usuarioId: cantidadInicial > 0 ? sesion?.usuarioId : undefined,
        }));
    setEnviando(false);
    if (res.ok) onSaved();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  // Sprint 13 — Preview en vivo: si la dueña pone 10 kg crudo con 30% merma,
  // muestra "Disponible para venta: 7.000 kg" para que entienda qué pasa.
  const rendimientoFactor = (100 - mermaCoccionPct) / 100;
  const disponibleVenta = cantidadInicial > 0 ? cantidadInicial * rendimientoFactor : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold">
            {insumo ? 'Editar ingrediente' : 'Nuevo ingrediente'}
          </h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar" disabled={enviando}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5 overflow-auto">
          <div>
            <label className="label" htmlFor="ins-nombre">¿Qué ingrediente es? *</label>
            <input
              id="ins-nombre"
              className="input"
              placeholder="Ej. Sirloin, Cogote, Pan, Refresco"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              autoFocus
              maxLength={50}
            />
          </div>
          <div>
            <label className="label" htmlFor="ins-unidad">¿Cómo lo compras?</label>
            <select
              id="ins-unidad"
              className="input"
              value={unidad}
              onChange={(e) => cambiarUnidad(e.target.value as UnidadInsumo)}
              /*
                Sprint 73 — Se puede cambiar mientras el ingrediente no tenga movimientos.
                Reporte del negocio: un "?" dibujado sobre este desplegable. El candado era correcto
                pero se aplicaba SIEMPRE, incluso en un ingrediente creado hace un minuto — que es
                justo cuando uno se da cuenta de que eligió mal la unidad.
              */
              disabled={Boolean(insumo?.tieneMovimientos)}
            >
              {unidades.map((u) => (
                <option key={u.codigo} value={u.codigo}>
                  {u.nombre}
                  {u.descripcion ? ` — ${u.descripcion.toLowerCase()}` : ''}
                </option>
              ))}
            </select>
            {insumo?.tieneMovimientos ? (
              <p className="help-text">
                Ya cargaste o vendiste <b>{insumo.nombre}</b> en <b>{insumo.unidad}</b>, así que la
                unidad ya no se puede cambiar: esas cantidades se leerían como si fueran de otra
                medida. Si está mal, crea un ingrediente nuevo con la unidad correcta y da de baja
                este.
              </p>
            ) : insumo ? (
              <p className="help-text">
                Todavía no tiene movimientos, así que puedes corregir la unidad. En cuanto cargues o
                vendas, queda fija.
              </p>
            ) : (
              <p className="help-text">
                ¿Compras por bolsa, caja o manojo? Se agregan en Configuración → Unidades de
                medida.
              </p>
            )}
          </div>

          {/*
            Sprint 61 — Cómo se puede usar. Va ANTES de la merma a propósito: si algo
            se entrega completo, la merma de cocción no tiene sentido, y verlo en ese
            orden lo hace evidente.
          */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <label className="label" htmlFor="ins-fraccion">
              ¿Cómo se puede usar?
            </label>
            <select
              id="ins-fraccion"
              className="input"
              value={fraccionMinima === null ? 'libre' : String(fraccionMinima)}
              onChange={(e) =>
                setFraccionMinima(e.target.value === 'libre' ? null : Number(e.target.value))
              }
            >
              <option value="libre">Cualquier cantidad (se pesa o se mide)</option>
              {opcionesFraccion.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-600">
              {fraccionMinima === null
                ? `El inventario podrá tener cualquier cantidad de ${unidad}.`
                : `El inventario se moverá en ${describirFraccion(fraccionMinima, unidad)}: nunca quedará una cantidad imposible.`}
            </p>
          </div>

          {/*
            Sprint 13 — Merma por cocción (atributo del insumo).

            Sprint 62 — No se pregunta cuando el insumo se entrega completo: una lata
            cruda es una lata cocida, así que el campo sólo servía para descontar
            inventario que no se pierde. Si un dato viejo trae merma sobre algo
            indivisible, el bloque SÍ aparece —con su aviso— para poder corregirlo.
            Esto quita una decisión de encima en el caso más común del catálogo
            (refrescos, pan, desechables).
          */}
          {(!esIndivisible(fraccionMinima) || mermaCoccionPct > 0) && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            {esIndivisible(fraccionMinima) && mermaCoccionPct > 0 && (
              <p className="mb-2 rounded-md bg-white/70 px-2 py-1.5 text-xs font-medium text-amber-900">
                Esto se entrega completo, y algo que no se abre no encoge al cocinarse. Si de todas
                formas se pierde producto (se rompe, se derrama), regístralo como merma del día en
                lugar de ponerlo aquí.
              </p>
            )}
            <label className="label" htmlFor="ins-merma">
              Merma por cocción (%) <span className="text-slate-400 font-normal">— deja 0 si no se cocina</span>
            </label>
            <div className="flex items-center gap-2">
              <NumberField
                id="ins-merma"
                min={0}
                max={99}
                decimals={1}
                className="input font-mono text-right flex-1"
                value={mermaCoccionPct}
                onChange={setMermaCoccionPct}
              />
              <span className="text-lg font-bold text-slate-600">%</span>
            </div>
            <p className="help-text">
              <strong>¿Cuánto se pierde al cocinar?</strong> Para carne típicamente 25-35%.
              Para pan, refresco, salsa → 0%.
              {mermaCoccionPct > 0 && (
                <span className="block mt-1 text-amber-800">
                  → De cada 1 {unidad} crudo, quedan {((100 - mermaCoccionPct) / 100).toFixed(2)} {unidad} para vender.
                </span>
              )}
            </p>
          </div>
          )}

          <div>
            <label className="label" htmlFor="ins-stockmin">Avísame cuando queden menos de</label>
            <NumberField
              id="ins-stockmin"
              min={0}
              decimals={3}
              className="input font-mono text-right"
              value={stockMinimo}
              onChange={setStockMinimo}
            />
            <p className="help-text">
              Debajo de esta cantidad, el insumo aparece en rojo en el inventario y en el tablero.
              Déjalo en 0 si no quieres avisos.
            </p>
          </div>

          {/* Cantidad inicial (solo al crear) */}
          {!insumo && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
              <label className="label" htmlFor="ins-inicial">
                ¿Cuánto tienes ahora de este insumo? <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <div className="flex items-center gap-2">
                <NumberField
                  id="ins-inicial"
                  min={0}
                  decimals={3}
                  className="input font-mono text-right flex-1"
                  value={cantidadInicial}
                  onChange={setCantidadInicial}
                />
                <span className="text-sm text-slate-600">{unidad} crudo</span>
              </div>
              {cantidadInicial > 0 && mermaCoccionPct > 0 && (
                <div className="mt-2 rounded bg-white p-2 text-center border border-blue-300">
                  <div className="text-xs text-slate-600">Disponible para vender (cocido)</div>
                  <div className="text-lg font-mono font-bold text-blue-700">
                    {disponibleVenta.toFixed(3)} {unidad}
                  </div>
                </div>
              )}
              <p className="help-text">
                Cantidad CRUDA con la que arrancas. Si lo dejas en 0, podrás cargar después.
              </p>
            </div>
          )}

          {insumo && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
              />
              <span>Activo (aparece en carga diaria y reportes)</span>
            </label>
          )}
          {error && <div className="error-text">{error}</div>}
        </div>
        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="btn-ghost" disabled={enviando}>Cancelar</button>
          <button onClick={submit} className="btn-primary" disabled={enviando}>
            {enviando ? 'Guardando...' : insumo ? 'Guardar cambios' : 'Crear insumo'}
          </button>
        </div>
      </div>
    </div>
  );
}
